import { useMemo } from 'react';
import './points.css';

/* ------------------------------------------------------------------
   Icons / small visual helpers
   (kept from the original component API — do not remove)
   ------------------------------------------------------------------ */

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M19 12H5M11 6l-6 6 6 6"
        stroke="#3d2f7a"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon({ light }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 5l7 7-7 7"
        stroke={light ? '#ffffff' : '#6c4ce8'}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SparkleIcon({ className, color = '#ff9f2e' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2c0 5.2-2.8 8-8 8 5.2 0 8 2.8 8 8 0-5.2 2.8-8 8-8-5.2 0-8-2.8-8-8Z" fill={color} />
    </svg>
  );
}

function DotIcon({ className, color = '#c9bdf2' }) {
  return <span className={className} style={{ background: color }} aria-hidden="true" />;
}

function DashIcon({ className }) {
  return (
    <svg className={className} width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true">
      <path d="M5 1v16" stroke="#c9bdf2" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function FilledStarIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 140 134" fill="none">
      <defs>
        <linearGradient id="starFill" x1="20" y1="0" x2="120" y2="134" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffd75e" />
          <stop offset="1" stopColor="#f5a623" />
        </linearGradient>
      </defs>
      <path
        d="M70 4 87 46 132 49 97 78 109 122 70 97 31 122 43 78 8 49 53 46Z"
        fill="url(#starFill)"
        stroke="#ffe9a8"
        strokeWidth="5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OutlineStarIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 140 134" fill="none">
      <path
        d="M70 4 87 46 132 49 97 78 109 122 70 97 31 122 43 78 8 49 53 46Z"
        fill="#f4f1fc"
        stroke="#c9bdf2"
        strokeWidth="4"
        strokeDasharray="6 6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BottomStarIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2 14.8 8.6 22 9.3 16.6 13.9 18.2 21 12 17.3 5.8 21 7.4 13.9 2 9.3 9.2 8.6Z"
        fill="#ffffff"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="10.5" width="14" height="10" rx="2.4" fill="#8b7fc8" />
      <path
        d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
        stroke="#8b7fc8"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* Number of subtle concentric rings drawn behind the badges, and which one
   is emphasised. Kept as named constants from the original file. */
const TOTAL_STEPS = 5;
const ACTIVE_STEP = 5;

const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];
const ordinalLabel = (n) => ORDINALS[n - 1] || `#${n}`;

export default function Points({ onBack, onContinue, userMissions = null, selectedJourneyId = null, totalStars = 2 }) {
  /* ----------------------------------------------------------------
     SINGLE SOURCE OF TRUTH
     ----------------------------------------------------------------
     Everything visual on this page is derived from `collectedShops`
     (and `totalStars`).

     collectedShops (Step 6E) is the Star Badge count — the number of
     the CURRENT journey's user missions that already have a real
     point transaction, computed directly from the already-loaded
     `userMissions` (never from journey-scoped App.js state like
     `collectedStars`, which can still read 0 right after re-login
     before its own restore effect has run). Each user mission is
     unique per user+shop, so this never double-counts a shop.

     totalStars keeps using the journey's existing `places_required`
     as the denominator — unchanged.

         collectedShops = 0  ->  ○ ○ ○   ·  0 / 3  ·  0%
         collectedShops = 1  ->  ⭐ ○ ○   ·  1 / 3  ·  33%
         collectedShops = 2  ->  ⭐ ⭐ ○   ·  2 / 3  ·  67%
         collectedShops = 3  ->  ⭐ ⭐ ⭐   ·  3 / 3  ·  100%

     Nothing else needs to be touched — badges, connectors, copy,
     percentage, progress bar and the CTA all recompute below.
     ---------------------------------------------------------------- */

  // Star Badge numerator: current-journey user missions with a real
  // point_transactions entry. Deliberately NOT `status === 'completed'`
  // — a mission can have current_step 4 and its point transaction
  // recorded while the overall journey is still incomplete.
  const collectedShops = useMemo(() => {
    if (!Array.isArray(userMissions) || selectedJourneyId == null) return 0;

    return userMissions.filter(
      (mission) =>
        mission.journey_id === selectedJourneyId &&
        Array.isArray(mission.point_transactions) &&
        mission.point_transactions.length > 0
    ).length;
  }, [userMissions, selectedJourneyId]);

    // Safe / clamped values — always use these for visual state, never
    // the raw `collectedShops` / `totalStars` values.
    const safeTotal = Math.max(1, totalStars);
    const safeCollected = Math.max(0, Math.min(collectedShops, safeTotal));

const progressPercent = Math.round((safeCollected / safeTotal) * 100);

const remainingBadges = Math.max(safeTotal - safeCollected,0
);

const isComplete = safeCollected >= safeTotal;

  // Build the dynamic badge progression: one column per star, with a
  // connector between each pair. Fully driven by `safeCollected`.
  //   badgeNumber <= safeCollected  -> filled gold badge
  //   badgeNumber >  safeCollected  -> locked lavender badge
  //   connector after badge N is "solid" only while  N < safeCollected
  const badgeRow = Array.from({ length: safeTotal }, (_, index) => {
    const badgeNumber = index + 1;
    const isCollected = badgeNumber <= safeCollected;
    const isNewest = isCollected && badgeNumber === safeCollected;
    const connectorComplete = badgeNumber < safeCollected;

    return [
      <div
        className={
          'points-badge-col ' +
          (isCollected ? 'points-badge-col-earned' : 'points-badge-col-locked') +
          (isNewest ? ' points-badge-col-newest' : '')
        }
        key={`badge-${badgeNumber}`}
      >
        <div className="points-badge-medal">
          {isCollected && <span className="points-badge-glow" />}
          {isCollected && <span className="points-badge-rays" />}
          <span className="points-ribbon points-ribbon-left" />
          <span className="points-ribbon points-ribbon-right" />
          <span className="points-badge-disc">
            {isCollected && <span className="points-badge-face" />}
            <span className="points-badge-star">
              {isCollected ? <FilledStarIcon /> : <OutlineStarIcon />}
            </span>
          </span>
          {!isCollected && (
            <span className="points-badge-lock">
              <LockIcon />
            </span>
          )}
        </div>
        {/* <span className="points-badge-label">{ordinalLabel(badgeNumber)}</span> */}
      </div>,
      badgeNumber < safeTotal ? (
        <span
          className={'points-badge-link ' + (connectorComplete ? 'is-solid' : 'is-dotted')}
          key={`link-${badgeNumber}`}
        />
      ) : null,
    ];
  });

  return (
    <div className="points-screen">
      <div className={'points-frame' + (isComplete ? ' points-frame-complete' : '')}>
        {/* Subtle concentric rings radiating from behind the badges */}
        <div className="points-rings" aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <span
              key={i}
              className={
                'points-ring-line' + (i + 1 === ACTIVE_STEP ? ' points-ring-line-active' : '')
              }
              style={{ '--ring-index': i + 1 }}
            />
          ))}
        </div>

        {/* Playful floating decorations */}
        <div className="points-decor" aria-hidden="true">
          <SparkleIcon className="points-deco points-deco-spark points-deco-spark-1" color="#f5a623" />
          <SparkleIcon className="points-deco points-deco-spark points-deco-spark-2" color="#f5a623" />
          <SparkleIcon className="points-deco points-deco-spark points-deco-spark-3" color="#f5a623" />
          <SparkleIcon className="points-deco points-deco-spark points-deco-spark-4" color="#c9bdf2" />
          <SparkleIcon className="points-deco points-deco-spark points-deco-spark-5" color="#f5a623" />

          <span className="points-deco points-deco-star points-deco-star-purple-1" />
          <span className="points-deco points-deco-star points-deco-star-purple-2" />
          <span className="points-deco points-deco-star points-deco-star-gold-1" />
          <span className="points-deco points-deco-star points-deco-star-gold-2" />

          <span className="points-deco points-deco-outstar">
            <OutlineStarIcon />
          </span>

          <span className="points-deco points-deco-square points-deco-square-pink" />
          <span className="points-deco points-deco-square points-deco-square-blue" />
          <span className="points-deco points-deco-tri" />

          <span className="points-deco points-deco-hollow points-deco-hollow-1" />
          <span className="points-deco points-deco-hollow points-deco-hollow-2" />

          <DotIcon className="points-deco points-deco-dot points-deco-dot-1" color="#c9bdf2" />
          <DotIcon className="points-deco points-deco-dot points-deco-dot-2" color="#8b6cf0" />

          <DashIcon className="points-deco points-deco-dash points-deco-dash-1" />

          <span className="points-deco points-deco-grid points-deco-grid-1" />
          <span className="points-deco points-deco-grid points-deco-grid-2" />
        </div>

        {/* Floating back button */}
        <div className="points-top">
          <button type="button" className="points-back-btn" aria-label="Go back" onClick={onBack}>
            <ArrowLeftIcon />
          </button>
        </div>

        <span className="points-eyebrow">
          <SparkleIcon className="points-eyebrow-spark" color="#f5a623" />
          Achievement Unlocked
          <SparkleIcon className="points-eyebrow-spark" color="#f5a623" />
        </span>

        <div className="points-body">
          <div className="points-hero-text">
            <span className="points-title-lead">You&rsquo;ve earned a</span>
            <h1 className="points-title">Star Badge!</h1>
          </div>

          {/* Dynamic badge progression — the hero element */}
          <div className="points-badges" aria-hidden="true">
            <div className="points-badges-row">{badgeRow}</div>
          </div>

          <p className="points-subtitle">
            {isComplete ? (
              <>All {safeTotal} badges collected! Your reward is ready.</>
            ) : (
              <>
                Collect{' '}
                <strong className="points-subtitle-em">{remainingBadges} more</strong>{' '}
                {remainingBadges === 1 ? 'badge' : 'badges'} to unlock your reward.
              </>
            )}
          </p>

          {/* Progress card — driven entirely by the safe values */}
          <div className="points-progress-card">
            <span className="points-progress-icon">
              <BottomStarIcon />
            </span>
            <span className="points-progress-main">
              <span className="points-progress-title">
                {safeCollected} / {safeTotal} Star Badges Collected
              </span>
              <span className="points-progress-row">
                <span className="points-progress-bar">
                  <span
                    className="points-progress-bar-fill"
                    style={{ width: progressPercent + '%' }}
                  />
                </span>
                {/* <span className="points-progress-percent">{progressPercent}%</span> */}
              </span>
              <span className="points-progress-subtitle">
                {remainingBadges === 0
                  ? 'All badges collected! Your reward is ready.'
                  : remainingBadges === 1
                  ? "You're almost there! Collect 1 more badge."
                  : `Keep completing challenges to collect all ${safeTotal} badges!`}
              </span>
            </span>
          </div>
        </div>

        <div className="points-actions">
          <button type="button" className="points-next-btn" onClick={onContinue}>
            {safeCollected === safeTotal ? 'Claim Reward' : 'Next'}
            <span className="points-next-icon">
              <ChevronRightIcon light />
            </span>
          </button>
          {/* <button type="button" className="points-later-btn" onClick={onBack}>
            Maybe Later
          </button> */}
        </div>
      </div>
    </div>
  );
}
